// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSettingsCollection } from "@bg/App/Store/Selectors/SettingsSelectors";
import Button, { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import Page from "@bg/MobiusOverrides/Page/Page";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { toTimestamp, xScreen } from "@bg/XDL/Utilities";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { css } from "styled-components";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeButton?: ButtonPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 250,
        cssOverrides: {
            drawerContainer: css`
                transform: none !important;
            `,
            drawerTitle: css`
                visibility: hidden;
                transform: none;
                min-height: 20px;
                max-height: 20px;
            `,
            drawerContent: css`
                padding: 25px 0;
                margin: 0 auto;
                width: 100%;
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
        `,
    },
    messageGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            padding: 30px 0 10px 0;
            display: flex;
            margin-top: 0;
            justify-content: flex-start;

            button {
                margin-right: 20px;
                &,
                span {
                    font-size: ${fontConfig.fontSize.small}px;
                    font-weight: ${fontConfig.weight.semiBold};
                    letter-spacing: 0;
                }
            }

            button + button {
                margin-right: 0;
                margin-left: 0;
            }

            ${xScreen("lte", "xs")} {
                justify-content: center;
                text-align: center;
                flex-wrap: wrap;
                padding-left: 0;
                padding-right: 0;
                margin-left: 0;
                margin-right: 0;

                button {
                    margin-right: 0;
                    margin-left: 0;
                    margin-bottom: 10px;
                    width: 100%;
                }
            }
        `,
    },

    closeButton: {
        variant: "secondary",
    },
    messageText: {
        size: fontConfig.fontSize.small,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const { location, cookiePrivacyPolicyDate } = useAppSelector(
        state => ({
            location: getLocation(state),
            cookiePrivacyPolicyDate: getSettingsCollection(state).customSettings.cookiePrivacyPolicyDate,
        }),
        shallowEqual,
    );

    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    /**
     * XNG-Changes
     * Check if policy date changed or not
     */
    useEffect(() => {
        const declineCookies = getCookie("declineCookies");
        const acceptCookies = getCookie("acceptCookies");
        const declineTimeStamp = Number(declineCookies) || 0;
        const acceptTimeStamp = Number(acceptCookies) || 0;
        const policyTimeStamp = toTimestamp(cookiePrivacyPolicyDate);

        if (
            enableCookiePrivacyPolicyPopup &&
            (acceptTimeStamp === 0 || policyTimeStamp > acceptTimeStamp) &&
            (declineTimeStamp === 0 || policyTimeStamp > declineTimeStamp)
        ) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        /**
         * XNG-Changes
         * Save the policeDate in acceptCookies
         */
        const policyTimeStamp = toTimestamp(cookiePrivacyPolicyDate);
        setCookie("acceptCookies", String(policyTimeStamp), { expires: 30 });
    };

    const declineHandler = () => {
        setIsOpen(false);
        /**
         * XNG-Changes
         * Save the policeDate in declineCookies
         */
        const policyTimeStamp = toTimestamp(cookiePrivacyPolicyDate);
        setCookie("declineCookies", String(policyTimeStamp), { expires: 30 });
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <Page>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.messageGridItem}>
                        <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                            {siteMessage("Core_CookiePrivacyPolicy")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem} className="modal-actions">
                        <Button
                            {...styles.acceptButton}
                            onClick={acceptHandler}
                            data-test-selector="cookiePolicy_AcceptCookies"
                        >
                            {siteMessage("Core_CookiePrivacyPolicyAccept")}
                        </Button>
                        <Button
                            {...styles.closeButton}
                            onClick={declineHandler}
                            aria-label={translate("Close")}
                            data-test-selector="cookiePolicy_ClosePolicy"
                        >
                            {translate("Decline")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Page>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
